import { useLocation, useNavigate } from 'react-router-dom';
import {
  BookOpenText, GraduationCap, ListChecks,
  CaretDoubleLeft, CaretDoubleRight, List, X,
} from '@phosphor-icons/react';
import { useTraining } from './TrainingContext';

/* ── Nav item definitions ── */

interface NavItemDef {
  key: string;
  label: string;
  icon: typeof GraduationCap;
  matchPath: string;
  buildPath: (ctx: { magicToken: string; catalogPath: string }) => string;
  requiresToken?: boolean;
}

const NAV_ITEMS: NavItemDef[] = [
  {
    key: 'catalog',
    label: 'Browse Events',
    icon: BookOpenText,
    matchPath: '/training/catalog',
    buildPath: ({ catalogPath }) => catalogPath,
  },
  {
    key: 'portal',
    label: 'My Registrations',
    icon: ListChecks,
    matchPath: '/training/portal',
    buildPath: ({ magicToken }) => `/training/portal?t=${magicToken}`,
    requiresToken: true,
  },
  {
    key: 'learner',
    label: 'Current Course',
    icon: GraduationCap,
    matchPath: '/training/registration',
    buildPath: () => '#',
    requiresToken: true,
  },
];

/* ── Sidebar ── */

export default function TrainingSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    org, orgLoading, learner, magicToken, catalogPath,
    sidebarCollapsed, setSidebarCollapsed,
    mobileSidebarOpen, setMobileSidebarOpen,
  } = useTraining();

  const collapsed = sidebarCollapsed;
  const orgInitial = org?.company_name?.charAt(0)?.toUpperCase() || 'T';
  const orgName = org?.company_name || 'Training';
  const learnerInitials = learner
    ? `${(learner.first_name || '')[0] || ''}${(learner.last_name || '')[0] || ''}`.toUpperCase()
    : null;

  const visibleItems = NAV_ITEMS.filter(
    (item) => !item.requiresToken || magicToken,
  );

  const filteredItems = visibleItems.filter((item) => {
    if (item.key === 'learner' && !location.pathname.startsWith('/training/registration')) return false;
    return true;
  });

  function isActive(matchPath: string) {
    return location.pathname.startsWith(matchPath);
  }

  function handleNav(item: NavItemDef) {
    const path = item.buildPath({ magicToken, catalogPath });
    if (path === '#') return;
    navigate(path);
    setMobileSidebarOpen(false);
  }

  /* ── Sidebar content (shared desktop + mobile) ── */
  const sidebarContent = (
    <div className="flex flex-col h-full" style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}>
      {/* ── Logo / Brand ── */}
      <div className={`flex items-center border-b border-gray-100 ${collapsed ? 'justify-center px-2 py-4' : 'justify-between px-6 py-5'}`}>
        <div className={`flex items-center gap-3 min-w-0 ${collapsed ? 'flex-col' : ''}`}>
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-[#4B45E5]">
            {org?.logo ? (
              <img src={org.logo} alt="" className="w-8 h-8 object-cover rounded-lg" />
            ) : (
              <span className="text-white font-semibold text-sm">{orgInitial}</span>
            )}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 leading-tight truncate">
                {orgLoading ? '...' : orgName}
              </div>
              <div className="text-[11px] text-gray-500 leading-tight">Training Portal</div>
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            onClick={() => setSidebarCollapsed(true)}
            className="p-1.5 rounded-md bg-transparent border-none cursor-pointer text-gray-500 hover:bg-gray-100 active:bg-gray-200 transition-all"
          >
            <CaretDoubleLeft size={14} weight="bold" />
          </button>
        )}
      </div>

      {/* ── Navigation ── */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto scrollbar-sidebar">
        {filteredItems.map((item) => {
          const active = isActive(item.matchPath);
          const Icon = item.icon;
          return (
            <button
              key={item.key}
              onClick={() => handleNav(item)}
              title={collapsed ? item.label : undefined}
              className={`
                w-full flex items-center gap-3 border-none cursor-pointer transition-colors duration-200
                ${collapsed ? 'justify-center px-2 h-10 rounded-md' : 'px-4 h-10 rounded-md'}
                ${active
                  ? 'bg-[#4B45E51A] text-[#4B45E5] font-semibold'
                  : 'bg-transparent text-gray-700 hover:bg-[#4B45E51A] hover:text-[#4B45E5]'}
              `}
            >
              <Icon
                size={20}
                weight={active ? 'fill' : 'regular'}
                className="flex-shrink-0"
              />
              {!collapsed && (
                <span className="text-sm leading-tight truncate">
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* ── Learner info (bottom) ── */}
      {learner && (
        <>
          <div className="mx-4 h-px bg-gray-100" />
          <div className={`px-3 py-4 ${collapsed ? 'flex justify-center' : ''}`}>
            <div className={`flex items-center gap-2.5 ${collapsed ? '' : 'px-1'}`}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-medium bg-[#B6B7E0] text-black">
                {learnerInitials}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-900 leading-tight truncate">
                    {learner.first_name} {learner.last_name}
                  </div>
                  <div className="text-[11px] text-gray-500 leading-tight truncate">
                    {learner.email}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Collapsed: expand toggle ── */}
      {collapsed && (
        <div className="hidden lg:block px-2 pb-4">
          <button
            onClick={() => setSidebarCollapsed(false)}
            className="w-full flex items-center justify-center py-2 rounded-md bg-transparent border-none cursor-pointer text-gray-500 hover:bg-gray-100 transition-all"
          >
            <CaretDoubleRight size={14} weight="bold" />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* ── Mobile hamburger trigger ── */}
      <button
        onClick={() => setMobileSidebarOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-[60] w-10 h-10 rounded-lg bg-white text-gray-700 flex items-center justify-center border border-gray-200 cursor-pointer shadow-sm hover:bg-gray-50 transition-colors"
        aria-label="Open menu"
      >
        <List size={20} weight="bold" />
      </button>

      {/* ── Desktop sidebar ── */}
      <aside
        className={`hidden lg:flex flex-col flex-shrink-0 h-screen sticky top-0 border-r border-gray-100 transition-all duration-300 ease-in-out z-[60]
          ${collapsed ? 'w-16' : 'w-64'}`}
        style={{
          background: 'linear-gradient(180deg, #EEF2FF 0%, #FAF5FF 50%, #FDF2F8 100%)',
        }}
      >
        {sidebarContent}
      </aside>

      {/* ── Mobile overlay sidebar ── */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-[70]">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div
            className="absolute left-0 top-0 bottom-0 w-64 flex flex-col border-r border-gray-100"
            style={{
              background: 'linear-gradient(180deg, #EEF2FF 0%, #FAF5FF 50%, #FDF2F8 100%)',
              animation: 'slideInLeft 200ms ease-out',
            }}
          >
            <button
              onClick={() => setMobileSidebarOpen(false)}
              className="absolute top-4 right-3 w-8 h-8 rounded-md bg-transparent text-gray-500 hover:bg-gray-100 flex items-center justify-center border-none cursor-pointer transition-colors"
            >
              <X size={16} weight="bold" />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInLeft {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </>
  );
}
